import { NetworkErrorWhiteList } from 'constants/networkErrorWhiteList';
import React, { useEffect, useRef, useCallback } from 'react';
import { useBreakpoint, useHover } from '@landr/maestro';
import { Pack, Repack, PackType } from 'types/generated/graphql';
import { EntityType } from 'types/enums';
import { analytics } from 'utils/analytics';
import {
    CtaGoal,
    CtaType,
    InteractionSource,
} from 'utils/analytics/AnalyticsV2Service.generated';
import useGetEntityDetailsLink from 'hooks/useGetEntityDetailsLink/useGetEntityDetailsLink';
import PackPreviewCard from 'components/Cards/PackPreviewCard/PackPreviewCard';
import { usePreviewContext } from '../contexts/PreviewContext/PreviewContext';
import useOptimisticQuery from '../contexts/PreviewContext/hooks/useOptimisticQuery';
import { PreviewShowDelay } from '../contexts/PreviewContext/constant';
import { useCarouselContext } from '../contexts/CarouselContext';

export const useSlide = ({
    item,
    isHovered,
    hoverRef,
    showPreview,
    optimisticQuery,
    contentGroup,
    contentList,
    isPreviewAllowed,
}: {
    item?: Pack | Repack;
    isHovered: boolean;
    hoverRef: React.MutableRefObject<HTMLDivElement | null>;
    showPreview: ({
        guid,
        type,
        slideRef,
    }: {
        guid: string;
        type: PackType;
        slideRef: React.MutableRefObject<HTMLDivElement | null>;
    }) => void;
    optimisticQuery: ({
        guid,
        type,
        signal,
    }: {
        guid: string;
        type: PackType;
        signal: AbortSignal;
    }) => void;
    contentGroup?: string;
    contentList?: string;
    isPreviewAllowed: boolean;
}) => {
    const isMobile = useBreakpoint('md');

    const timerRef = useRef<number | undefined>(undefined);

    const getEntityDetailsLink = useGetEntityDetailsLink();

    const linkTo = getEntityDetailsLink(item?.type, item?.slug);
    const bylineLinkTo = getEntityDetailsLink(
        EntityType.Label,
        item?.label?.slug,
    );

    const isExclusive = item?.isExclusive as boolean;
    const artworkUrl = item?.artworkUrl as string;
    const title = item?.name as string;
    const byline = item?.label?.name as string;
    const backgroundColor = (item?.backgroundColor as string) ?? 'surface.low';

    useEffect(() => {
        const abortController = new AbortController();

        if (item?.guid && item?.type) {
            optimisticQuery({
                guid: item.guid,
                type: item.type,
                signal: abortController.signal,
            });
        }

        return () => {
            abortController.abort(NetworkErrorWhiteList.AbortController);
        };
    }, [item?.guid, item?.type, optimisticQuery]);

    useEffect(() => {
        if (isPreviewAllowed && isHovered && item?.guid && item?.type) {
            timerRef.current = window.setTimeout(() => {
                showPreview({
                    guid: item.guid,
                    type: item.type,
                    slideRef: hoverRef,
                });
            }, PreviewShowDelay);
        }

        return () => {
            window.clearTimeout(timerRef.current);
        };
    }, [
        backgroundColor,
        isHovered,
        showPreview,
        hoverRef,
        item?.guid,
        item?.type,
        isPreviewAllowed,
    ]);

    const onLinkClick = useCallback(() => {
        analytics.trackCTAClicked({
            ctaGoal: CtaGoal.ViewDetail,
            ctaType: CtaType.PlainText,
            interactionSource: InteractionSource.CardAction,
            ctaDestination: linkTo,
            ctaContentGroup: contentGroup,
            ctaContentList: contentList,
        });
    }, [contentGroup, contentList]);

    const showCardPlayer = !isPreviewAllowed;
    const showPurchaseCta = !isPreviewAllowed && !isMobile;
    const showFavoriteCta = !isPreviewAllowed && !isMobile;

    return {
        linkTo,
        bylineLinkTo,
        title,
        byline,
        artworkUrl,
        isExclusive,
        backgroundColor,
        onLinkClick,
        showCardPlayer,
        showPurchaseCta,
        showFavoriteCta,
    };
};

export const SlideSkeleton = () => {
    return <PackPreviewCard isLoading={true} />;
};

const Slide = ({ item }: { item?: Pack | Repack }) => {
    const [hoverRef, isHovered] = useHover(false);
    const { isPreviewAllowed } = useCarouselContext();
    const { showPreview, contentGroup, contentList } = usePreviewContext();
    const optimisticQuery = useOptimisticQuery();

    const {
        linkTo,
        bylineLinkTo,
        artworkUrl,
        title,
        byline,
        isExclusive,
        backgroundColor,
        onLinkClick,
    } = useSlide({
        item,
        isHovered,
        showPreview,
        hoverRef,
        optimisticQuery,
        contentGroup,
        contentList,
        isPreviewAllowed,
    });

    if (!item) {
        return <SlideSkeleton />;
    }

    return (
        <PackPreviewCard
            ref={hoverRef}
            isExclusive={isExclusive}
            backgroundColor={backgroundColor}
            linkTo={linkTo}
            bylineLinkTo={bylineLinkTo}
            title={title}
            byline={byline}
            item={item}
            artworkUrl={artworkUrl}
            isTakenDownUIVisible={false}
            onLinkClick={onLinkClick}
            isHovered={isHovered}
        />
    );
};

export default Slide;
